// Simple, composed here because its ad step joins two features (spec ui-perf/simple-studio, PR
// s3): Simple's page (`features/studio2/simple`) with the templates area's own form panel
// (`features/templates`), unchanged. A feature never imports another; the composition root may.
import { createRoute, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import type { Job, TemplateCard } from "../api/generated/model";
import { studio2Route } from "../features/studio2/routes";
import { SimplePage } from "../features/studio2/simple/SimplePage";
import { TemplateCards, TemplateFormCard } from "../features/studio2/simple/TemplateCards";
import { FORMS } from "../features/templates/forms";
import { TemplatePanel } from "../features/templates/TemplatePanel";
import { InlinePanels } from "../ui/GlassPanel";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/home2/simple";

// `?kind=`: what Simple makes; anything else is none chosen yet.
const simpleSearch = z.object({
  kind: z.enum(["image", "video", "ad"]).optional().catch(undefined),
});

export const studio2SimpleRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "simple",
  validateSearch: (search) => simpleSearch.parse(search),
  component: ProSimple,
});

function ProSimple() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Another brand starts the page anew: no draft, template or result of the last brand's.
  return (
    <SimplePage
      key={`${teamId} ${brandId}`}
      template={(onStarted) => <SimpleTemplate teamId={teamId} brandId={brandId} onStarted={onStarted} />}
    />
  );
}

/** A template with a form built here: the rest open nothing, as their cards on the Templates page. */
const usable = (template: TemplateCard) => FORMS[template.id] !== undefined;

/** The templates, then a chosen one's own form in the page's flow; its runs go to Simple's result. */
function SimpleTemplate(props: { teamId: string; brandId: string; onStarted: (job: Job) => void }) {
  const { teamId, brandId, onStarted } = props;
  const [chosen, setChosen] = useState<TemplateCard | null>(null);
  if (chosen === null) return <TemplateCards teamId={teamId} usable={usable} onPick={setChosen} />;
  return (
    <TemplateFormCard onBack={() => setChosen(null)}>
      <InlinePanels value>
        <TemplatePanel
          key={chosen.id}
          teamId={teamId}
          brandId={brandId}
          template={chosen}
          replicate={false}
          onClose={() => setChosen(null)}
          onStarted={onStarted}
        />
      </InlinePanels>
    </TemplateFormCard>
  );
}
